import { type StateCreator } from "zustand";
import { createStore } from "zustand/vanilla";
import type { DevicePorts } from "../../../runtime/ports";
import type { PlayerSliceDeps } from "./deps";
import { persist, createJSONStorage } from "zustand/middleware";
import { devtools } from "@csark0812/zustand-expo-devtools";
import { immer } from "zustand/middleware/immer";
import logger from "zustand-logger-middleware";
import { captureException } from "@utils/captureException";
import { getZustandPersistStorageDiagnostics } from "@utils/zustandPersist";

import type { PlayerStore } from "./types";
import { createPlaybackSlice } from "./slices/playback.slice";
import { createQueueSlice } from "./slices/queue.slice";
import { createRecommendationsSlice } from "./slices/recommendations.slice";
import { createPreferencesSlice } from "./slices/preferences.slice";
import { shouldFetchMorePlaylistRecommendations } from "../utils/queue.utils";
import { logPlayerStoreAction } from "./storeLogger";
import { migrateLegacyPlayerPersistedState } from "./storeMigration";

const PLAYER_STORE_PERSIST_KEY = "lissen-player-v2";
const PLAYER_STORE_HYDRATION_TIMEOUT_MS = 3_000;

const getQueueHydrationSnapshot = (
  queue: PlayerStore["playlistQueue"] | PlayerStore["foryouQueue"],
) => {
  const currentItem =
    queue && queue.currentIndex >= 0 ? queue.items[queue.currentIndex] : null;

  return {
    currentIndex: queue?.currentIndex ?? null,
    currentQueueItemId: currentItem?.qid ?? null,
    currentSongId: currentItem?.metadata.id ?? null,
    hasCurrentItem: Boolean(currentItem),
    itemCount: queue?.items.length ?? 0,
  };
};

const getHydrationStateSnapshot = (state?: PlayerStore | null) => {
  if (!state) {
    return {
      hasState: false,
    };
  }

  return {
    activeQueueType: state.activeQueueType,
    foryouQueue: getQueueHydrationSnapshot(state.foryouQueue),
    hasActions: Boolean(state.actions),
    hasSetHasHydrated: typeof state.setHasHydrated === "function",
    hasState: true,
    hasHydrated: state._hasHydrated,
    lastFetchedSongId: state.lastFetchedSongId,
    lastViewedSongId: state.lastViewedSongId,
    paginationUserId: state.paginationUserId,
    playlistQueue: getQueueHydrationSnapshot(state.playlistQueue),
    playlistRecommendationCount: (state.playlistQueue?.items ?? []).filter(
      (item) => item.source === "playlist-recommendation",
    ).length,
    status: state.status,
    userIntent: state.userIntent,
  };
};

export function createPlayerStore(
  deps: PlayerSliceDeps,
  kv: DevicePorts["kv"],
) {
  let playerStoreHydrationDiagnostics: Record<string, unknown> = {
    key: PLAYER_STORE_PERSIST_KEY,
    timeoutMs: PLAYER_STORE_HYDRATION_TIMEOUT_MS,
  };

  const getPlayerStoreHydrationDiagnostics = () =>
    playerStoreHydrationDiagnostics;

  const getRecord = (value: unknown): Record<string, unknown> =>
    typeof value === "object" && value !== null
      ? (value as Record<string, unknown>)
      : {};

  const getNumber = (record: Record<string, unknown>, key: string) =>
    typeof record[key] === "number" ? record[key] : null;

  const getString = (record: Record<string, unknown>, key: string) =>
    typeof record[key] === "string" ? record[key] : null;

  const getBoolean = (record: Record<string, unknown>, key: string) =>
    typeof record[key] === "boolean" ? record[key] : null;

  const getStorageDiagnosticsForSentry = (
    prefix: string,
    value: unknown,
  ): Record<string, unknown> => {
    const storage = getRecord(value);
    const foryouQueue = getRecord(storage.persistedForyouQueue);
    const playlistQueue = getRecord(storage.persistedPlaylistQueue);

    return {
      [`${prefix}ErrorMessage`]: getString(storage, "errorMessage"),
      [`${prefix}ErrorName`]: getString(storage, "errorName"),
      [`${prefix}ParseOk`]: getBoolean(storage, "parseOk"),
      [`${prefix}PersistedActiveQueueType`]: getString(
        storage,
        "persistedActiveQueueType",
      ),
      [`${prefix}PersistedForyouCurrentIndex`]: getNumber(
        foryouQueue,
        "currentIndex",
      ),
      [`${prefix}PersistedForyouCurrentSongId`]: getString(
        foryouQueue,
        "currentSongId",
      ),
      [`${prefix}PersistedForyouHasCurrentItem`]: getBoolean(
        foryouQueue,
        "hasCurrentItem",
      ),
      [`${prefix}PersistedForyouItemCount`]: getNumber(
        foryouQueue,
        "itemCount",
      ),
      [`${prefix}PersistedHasHydrated`]: getBoolean(
        storage,
        "persistedHasHydrated",
      ),
      [`${prefix}PersistedPlaylistCurrentIndex`]: getNumber(
        playlistQueue,
        "currentIndex",
      ),
      [`${prefix}PersistedPlaylistCurrentSongId`]: getString(
        playlistQueue,
        "currentSongId",
      ),
      [`${prefix}PersistedPlaylistHasCurrentItem`]: getBoolean(
        playlistQueue,
        "hasCurrentItem",
      ),
      [`${prefix}PersistedPlaylistItemCount`]: getNumber(
        playlistQueue,
        "itemCount",
      ),
      [`${prefix}PersistedPlaylistRecommendationCount`]: getNumber(
        storage,
        "persistedPlaylistRecommendationCount",
      ),
      [`${prefix}RawExists`]: getBoolean(storage, "rawExists"),
      [`${prefix}RawLength`]: getNumber(storage, "rawLength"),
      [`${prefix}ReadOk`]: getBoolean(storage, "readOk"),
      [`${prefix}Version`]: getNumber(storage, "version"),
    };
  };

  const getStateDiagnosticsForSentry = (
    prefix: string,
    value: unknown,
  ): Record<string, unknown> => {
    const state = getRecord(value);
    const foryouQueue = getRecord(state.foryouQueue);
    const playlistQueue = getRecord(state.playlistQueue);

    return {
      [`${prefix}ActiveQueueType`]: getString(state, "activeQueueType"),
      [`${prefix}ForyouItemCount`]: getNumber(foryouQueue, "itemCount"),
      [`${prefix}HasHydrated`]: getBoolean(state, "hasHydrated"),
      [`${prefix}HasState`]: getBoolean(state, "hasState"),
      [`${prefix}PlaylistItemCount`]: getNumber(playlistQueue, "itemCount"),
      [`${prefix}PlaylistRecommendationCount`]: getNumber(
        state,
        "playlistRecommendationCount",
      ),
      [`${prefix}Status`]: getString(state, "status"),
      [`${prefix}UserIntent`]: getString(state, "userIntent"),
    };
  };

  const getPlayerStoreHydrationFlatDiagnostics = () => {
    const diagnostics = getRecord(playerStoreHydrationDiagnostics);
    const finish = getRecord(diagnostics.finish);
    const finishError = getRecord(finish.error);
    const timeout = getRecord(diagnostics.timeout);

    return {
      playerStoreHydrationFinishElapsedMs: getNumber(finish, "elapsedMs"),
      playerStoreHydrationFinishErrorMessage: getString(finishError, "message"),
      playerStoreHydrationFinishErrorName: getString(finishError, "name"),
      playerStoreHydrationFinishedAfterTimeout: getBoolean(
        finish,
        "finishedAfterTimeout",
      ),
      playerStoreHydrationFinishedAtMs: getNumber(finish, "finishedAtMs"),
      playerStoreHydrationKey: getString(diagnostics, "key"),
      playerStoreHydrationPhase: getString(diagnostics, "phase"),
      playerStoreHydrationStartedAtMs: getNumber(diagnostics, "startedAtMs"),
      playerStoreHydrationTimeoutElapsedMs: getNumber(timeout, "elapsedMs"),
      playerStoreHydrationTimedOutAtMs: getNumber(timeout, "timedOutAtMs"),
      playerStoreHydrationTimeoutMs: getNumber(diagnostics, "timeoutMs"),
      ...getStateDiagnosticsForSentry(
        "playerStoreHydrationStartState",
        diagnostics.startState,
      ),
      ...getStateDiagnosticsForSentry(
        "playerStoreHydrationFinishState",
        finish.callbackState,
      ),
      ...getStateDiagnosticsForSentry(
        "playerStoreHydrationTimeoutState",
        timeout.state,
      ),
      ...getStorageDiagnosticsForSentry(
        "playerStoreHydrationStartStorage",
        diagnostics.startStorageDiagnostics,
      ),
      ...getStorageDiagnosticsForSentry(
        "playerStoreHydrationFinishStorage",
        finish.storageDiagnostics,
      ),
      ...getStorageDiagnosticsForSentry(
        "playerStoreHydrationTimeoutStorage",
        timeout.storageDiagnostics,
      ),
    };
  };

  const capturePlayerStoreHydration = ({
    channel,
    error,
    extra,
    result,
  }: {
    channel: "breadcrumb" | "error";
    error: unknown;
    extra?: Record<string, unknown>;
    result: string;
  }) => {
    captureException(error, {
      channel,
      area: "player",
      operation: "player-store-hydration-v2",
      fingerprint: ["player-store-hydration-v2", result],
      tags: {
        persistKey: PLAYER_STORE_PERSIST_KEY,
        result,
      },
      extra: {
        ...getPlayerStoreHydrationFlatDiagnostics(),
        hydrationDiagnostics: playerStoreHydrationDiagnostics,
        ...extra,
      },
    });
  };

  // Saving the player is optional; playing is not. A write the device refuses
  // (on web a full or unavailable localStorage, LISSEN-APP-C1) must not throw
  // out of the `set` that made it: that `set` can be the track-end handler's,
  // which then never advances the queue. The store keeps the new state in
  // memory, storage keeps the last snapshot it accepted, and every later
  // change tries again. The first refusal after a saved write reports once,
  // with the snapshot's size, so a run of refused writes is one event.
  let persistWriteRefused = false;
  const persistSetItem = (key: string, value: string) => {
    try {
      kv.set(key, value);
      persistWriteRefused = false;
    } catch (error) {
      if (persistWriteRefused) return;
      persistWriteRefused = true;
      captureException(error, {
        channel: "error",
        area: "player",
        operation: "player-store-persist",
        fingerprint: ["player-store-persist"],
        tags: { persistKey: key },
        extra: {
          snapshotLength: value.length,
          savedSnapshot: getZustandPersistStorageDiagnostics(key, kv),
        },
      });
    }
  };

  const storeCreator: StateCreator<
    PlayerStore,
    [["zustand/immer", never], ["zustand/devtools", never]],
    []
  > = (set, get, store) => {
    const playback = createPlaybackSlice(deps)(set, get, store);
    const queue = createQueueSlice(deps)(set, get, store);
    const recommendations = createRecommendationsSlice(deps)(set, get, store);
    const preferences = createPreferencesSlice(deps)(set, get, store);

    return {
      ...playback,
      ...queue,
      ...recommendations,
      ...preferences,
      _hasHydrated: false,
      setHasHydrated: (state: boolean) => {
        set(
          (s) => {
            s._hasHydrated = state;
          },
          false,
          "store/setHasHydrated",
        );
      },
      actions: {
        ...playback.actions,
        ...queue.actions,
        ...recommendations.actions,
        ...preferences.actions,
      },
    };
  };

  const store = createStore<PlayerStore>()(
    devtools(
      persist(
        immer(
          logger(storeCreator, (fnName: string, fnArgs: unknown) => {
            logPlayerStoreAction(fnName, fnArgs);
          }),
        ),
        {
          name: PLAYER_STORE_PERSIST_KEY,
          storage: createJSONStorage(() => ({
            getItem: (key) => kv.getString(key) ?? null,
            setItem: persistSetItem,
            removeItem: (key) => kv.remove(key),
          })),
          merge: (persistedState, currentState) => {
            const migratedState =
              migrateLegacyPlayerPersistedState(persistedState);

            return migratedState
              ? { ...currentState, ...migratedState }
              : currentState;
          },
          onRehydrateStorage: (initialState) => {
            const startedAtMs = deps.now();
            let timeoutFired = false;
            const startStorageDiagnostics = getZustandPersistStorageDiagnostics(
              PLAYER_STORE_PERSIST_KEY,
              kv,
            );

            playerStoreHydrationDiagnostics = {
              key: PLAYER_STORE_PERSIST_KEY,
              phase: "started",
              startedAtMs,
              startState: getHydrationStateSnapshot(initialState),
              startStorageDiagnostics,
              timeoutMs: PLAYER_STORE_HYDRATION_TIMEOUT_MS,
            };

            capturePlayerStoreHydration({
              channel: "breadcrumb",
              error: "Player store hydration v2 started",
              result: "started",
            });

            const timeout = setTimeout(() => {
              timeoutFired = true;
              const timedOutAtMs = deps.now();
              const timeoutStorageDiagnostics =
                getZustandPersistStorageDiagnostics(
                  PLAYER_STORE_PERSIST_KEY,
                  kv,
                );

              playerStoreHydrationDiagnostics = {
                ...playerStoreHydrationDiagnostics,
                phase: "timeout",
                timeout: {
                  elapsedMs: timedOutAtMs - startedAtMs,
                  state: getHydrationStateSnapshot(store.getState()),
                  storageDiagnostics: timeoutStorageDiagnostics,
                  timedOutAtMs,
                },
              };

              capturePlayerStoreHydration({
                channel: "error",
                error: new Error("Player store hydration v2 timeout"),
                result: "timeout",
              });
            }, PLAYER_STORE_HYDRATION_TIMEOUT_MS);

            return (state, error) => {
              clearTimeout(timeout);
              const finishedAtMs = deps.now();
              const finishStorageDiagnostics =
                getZustandPersistStorageDiagnostics(
                  PLAYER_STORE_PERSIST_KEY,
                  kv,
                );

              playerStoreHydrationDiagnostics = {
                ...playerStoreHydrationDiagnostics,
                finish: {
                  callbackState: getHydrationStateSnapshot(state),
                  elapsedMs: finishedAtMs - startedAtMs,
                  error:
                    error instanceof Error
                      ? {
                          message: error.message,
                          name: error.name,
                          stack: error.stack,
                        }
                      : error
                        ? { message: String(error), name: typeof error }
                        : null,
                  finishedAfterTimeout: timeoutFired,
                  finishedAtMs,
                  storageDiagnostics: finishStorageDiagnostics,
                },
                phase: error
                  ? "error"
                  : timeoutFired
                    ? "finished-after-timeout"
                    : "finished",
              };

              if (error) {
                capturePlayerStoreHydration({
                  channel: "error",
                  error,
                  result: "error",
                });
                return;
              }

              state?.setHasHydrated(true);

              if (timeoutFired) {
                capturePlayerStoreHydration({
                  channel: "error",
                  error: new Error(
                    "Player store hydration v2 finished after timeout",
                  ),
                  result: "finished-after-timeout",
                });
              } else {
                capturePlayerStoreHydration({
                  channel: "breadcrumb",
                  error: "Player store hydration v2 finished",
                  result: "finished",
                });
              }

              if (
                state?.activeQueueType === "playlist" &&
                state.repeatMode === "off" &&
                state.playlistQueue &&
                shouldFetchMorePlaylistRecommendations(
                  state.playlistQueue.currentIndex,
                  state.playlistQueue.items.length,
                )
              ) {
                state.actions
                  .fetchMorePlaylistRecommendations()
                  .catch(console.error);
              }
            };
          },
          partialize: (state) => ({
            volume: state.volume,
            repeatMode: state.repeatMode,
            shuffleEnabled: state.shuffleEnabled,
            skipTimestamps: state.skipTimestamps,
            playlistQueue: state.playlistQueue,
            playlistRecsCursorSongId: state.playlistRecsCursorSongId,
            activeQueueType: state.activeQueueType,
            lastFetchedSongId: state.lastFetchedSongId,
            lastViewedSongId: state.lastViewedSongId,
            paginationUserId: state.paginationUserId,
          }),
        },
      ),
      { name: "LissenPlayer" },
    ),
  );

  return {
    store,
    getPlayerStoreHydrationDiagnostics,
    getPlayerStoreHydrationFlatDiagnostics,
  };
}
